import * as React from "react";
import { createPortal } from "react-dom";
import { remove } from "ramda";
import styled from "styled-components";

import posed, { PoseGroup } from "react-pose";

const Background = styled(
  posed.div({
    enter: {
      opacity: 1,
      // beforeChildren: true,
      transition: {
        // type: "spring",
        // stiffness: 400,
        // damping: 30
        duration: 150
      }
    },
    exit: {
      opacity: 0,
      // afterChildren: true,
      transition: {
        // type: "spring",
        // stiffness: 400,
        // damping: 30,
        duration: 150
      }
    }
  })
)`
  position: "fixed";
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.6);
`;

type ModalContextType = {
  showModal: (element: React.ReactElement<any>, closeOnBlur?: boolean) => void;
  hideModal: () => void;
};

export const ModalContext = React.createContext({} as ModalContextType);

type Props = {};

type State = {
  modals: Array<{
    element: React.ReactElement<any>;
    closeOnBlur: boolean;
  }>;
  contextPayload: ModalContextType;
};

class LayerManager extends React.Component<Props, State> {
  element: HTMLDivElement;
  backgroundRef = React.createRef<HTMLDivElement>();

  constructor(props: Props) {
    super(props);

    this.element = document.createElement("div");
    this.element.style.position = "fixed";
    this.element.style.top = "0px";
    this.element.style.left = "0px";
    this.element.style.zIndex = "2000";
    document.body.appendChild(this.element);

    this.state = {
      modals: [],
      contextPayload: {
        showModal: this.handleShowModal,
        hideModal: this.handleHideModal
      }
    };

    document.addEventListener("keydown", this.handleKeyPress);
  }

  componentWillUnmount() {
    document.body.removeChild(this.element);
    document.removeEventListener("keydown", this.handleKeyPress);
  }

  handleKeyPress = (evt: KeyboardEvent) => {
    if (
      this.state.modals.length > 0 &&
      evt.key === "Escape" &&
      this.shouldCloseOnBlur()
    ) {
      this.handleHideModal();
    }
  };

  handleShowModal = (
    element: React.ReactElement<any>,
    closeOnBlur: boolean = true
  ) => {
    if (this.state.modals.length === 0) {
      document.body.style.overflow = "hidden";
    }

    this.setState({
      modals: this.state.modals.concat({
        element,
        closeOnBlur
      })
    });
  };

  handleHideModal = () => {
    if (this.state.modals.length <= 1) {
      document.body.style.overflow = null;
    }

    this.setState({
      modals: remove(this.state.modals.length - 1, 1, this.state.modals)
    });
  };

  getModalStyle = (index: number, totalItems: number) => {
    const totalIndex = totalItems - 1;

    return {
      position: "absolute",
      left: "50%",
      top: "50%",
      filter:
        index === totalItems - 1
          ? null
          : `brightness(${1 - (totalIndex - index) * 0.25})`,
      boxShadow: "rgba(0, 0, 0, 0.25) 3px 4px 20px 0px"
    };
  };

  shouldCloseOnBlur = () => {
    const { modals } = this.state;
    return modals[modals.length - 1].closeOnBlur;
  };

  handleBackgroundClick = (evt: React.MouseEvent) => {
    if (this.shouldCloseOnBlur() && evt.target === this.backgroundRef.current) {
      this.handleHideModal();
    }
  };

  renderModals() {
    const { modals } = this.state;

    return (
      <PoseGroup flipMove={false}>
        {modals.length > 0 && (
          <Background
            ref={this.backgroundRef}
            onClick={this.handleBackgroundClick}
            key="background"
          >
            {modals.map((modal, index) =>
              React.cloneElement(modal.element, {
                key: `modal-${index}`,
                index,
                style: this.getModalStyle(index, modals.length)
              })
            )}
          </Background>
        )}
      </PoseGroup>
    );
  }

  render() {
    const { children } = this.props;
    const modals = this.renderModals();

    return (
      <ModalContext.Provider value={this.state.contextPayload}>
        {children}
        {createPortal(modals, this.element)}
      </ModalContext.Provider>
    );
  }
}

export default LayerManager;
